/**
 * Os rótulos do retorno da autorização (`TP-056`): o que a carta da SIAI diz, na sequência real do Bradesco. A
 * autorização a implantação responde ao orçamento; a liberação da 1ª diária vem depois da implantação; a pendência
 * não é negativa: a vez é da Anery.
 */

import { ErroDaApi } from '../api/erros';
import { formatarDataHora, type Instante } from '../dominio/datas';
import type { MotivoDoDescarte, OrigemDoResultado, PropostaDoRetorno, ResultadoDoRetorno } from './tipos';

export const ROTULOS_DO_RESULTADO: Record<ResultadoDoRetorno, string> = {
  Authorized: 'Autorizado a implantação',
  FirstDayReleased: '1ª diária liberada',
  Extended: 'Prorrogação',
  PendingDocuments: 'Pendência (pede documento)',
  Denied: 'Negado',
};

export const RESULTADOS_PARA_ESCOLHA = Object.keys(ROTULOS_DO_RESULTADO) as readonly ResultadoDoRetorno[];

export const ROTULOS_DA_PROPOSTA: Record<PropostaDoRetorno, string> = {
  Authorize: 'Autorizar o caso',
  Decline: 'Recusar o caso',
  OpenQuestion: 'Abrir a vez da Anery',
  RecordOnly: 'Só registrar (sem transição possível)',
};

export const ROTULOS_DO_MOTIVO: Record<MotivoDoDescarte, string> = {
  WrongReading: 'O resultado não é o que a carta diz',
  AlreadyHandled: 'Já foi tratado à mão',
  NotAReturn: 'Não é retorno de autorização',
  Other: 'Outro motivo',
};

export const MOTIVOS_PARA_ESCOLHA = Object.keys(ROTULOS_DO_MOTIVO) as readonly MotivoDoDescarte[];

/** Quem disse o resultado, com a confiança do agente quando houver. Texto, não só cor. */
export function rotuloDaOrigem(origem: OrigemDoResultado, confianca: number | null): string {
  if (origem === 'Human') return 'marcado por uma pessoa';
  // A máquina lê a carta (OCR da imagem + regra). Sem confiança = carta ambígua, vai para revisão.
  return confianca === null ? 'lido da carta (ambígua, confira)' : 'lido da carta';
}

/** De onde vem a hora que a transição grava, quando ela não é a chegada do e-mail. */
function origemDaHora(recebidoEm: Instante, hora: Instante): string {
  if (hora.getTime() > recebidoEm.getTime()) return 'entrega à operadora: o e-mail chegou antes dela';
  if (hora.getTime() < recebidoEm.getTime()) return 'agora: a chegada do e-mail está adiante do relógio';
  return 'chegada do e-mail';
}

/**
 * O texto do botão de confirmar: diz o que vai acontecer e QUAL HORA vai ser gravada. A autorização e a recusa valem
 * da chegada do e-mail, não do clique (D5) — e a pessoa precisa ver isso antes de clicar. A hora é a que o servidor
 * diz que vai gravar (`efetivoEm`): a chegada, limitada pela entrega à operadora e pelo relógio (DI2).
 */
export function textoDaConfirmacao(
  proposta: PropostaDoRetorno,
  recebidoEm: Instante,
  efetivoEm: Instante | null,
): string {
  const hora = efetivoEm ?? recebidoEm;
  switch (proposta) {
    case 'Authorize':
      return `Confirmar: autorização às ${formatarDataHora(hora)} (${origemDaHora(recebidoEm, hora)})`;
    case 'Decline':
      return `Confirmar: recusa às ${formatarDataHora(hora)} (${origemDaHora(recebidoEm, hora)})`;
    case 'OpenQuestion':
      return `Confirmar: vez da Anery desde ${formatarDataHora(recebidoEm)} (chegada do e-mail)`;
    case 'RecordOnly':
      return 'Confirmar: só registrar';
  }
}

/** Mensagem de cada desfecho, pelo `codigo` — mesma disciplina de `EsperasDoCaso`. */
export function mensagemDeErroDoRetorno(causa: unknown): string {
  if (causa instanceof ErroDaApi) {
    if (causa.codigo === 'RETURN_ALREADY_DECIDED')
      return 'Este retorno já foi decidido por outra pessoa, ou o resultado dele mudou. Recarregue para ver o que ficou.';
    if (causa.codigo === 'QUESTION_NOT_OPEN') return 'A vez da Anery desta pendência já foi fechada.';
    if (causa.codigo === 'RETURN_NOT_FOUND') return 'Este e-mail não é um retorno deste caso.';
    if (causa.codigo === 'INVALID_STATE_TRANSITION') return 'O caso mudou de estado e esta transição não vale mais.';
    if (causa.codigo === 'CASE_CONCURRENT_UPDATE')
      return 'O caso mudou enquanto você agia. Recarregue para ver o estado atual.';
    if (causa.status === 404) return 'Não encontrado para o seu núcleo.';
    if (causa.status === 400) return 'A API recusou o pedido.';
  }
  return 'Não foi possível concluir a ação. Tente novamente.';
}
